import { FC, useCallback, useState } from 'react';
import Papa from 'papaparse';
import { Controller, useFormContext } from 'react-hook-form';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { useTranslation } from 'react-i18next';
import { UploadArea } from './UploadArea';
import { useAppDispatch } from '../../hooks/useAppStore';
import { onboardingActions } from '../../store/onboarding/slice';
import { TMinimalMember } from '../../types';
import { useOptions } from '../../hooks/useOptions';

type TUploadCSV = {
  name: string;
};

const UploadCSV: FC<TUploadCSV> = ({ name, ...other }) => {
  const { control, setValue, getValues } = useFormContext();
  const [isOpenAlert, setIsOpenAlert] = useState(false);
  const dispatch = useAppDispatch();
  const { roleLowerMapNameToId } = useOptions();
  const { t } = useTranslation('common');

  const handleDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];

      if (file) {
        Papa.parse(file, {
          header: true,
          skipEmptyLines: true,
          complete: (result) => {
            const employees = result.data as Array<TMinimalMember>;
            const headers = result.meta.fields;

            const expectedHeaders = ['name', 'email', 'permission'];
            const hasAllHeaders = expectedHeaders.every((header) => headers?.includes(header));

            if (!hasAllHeaders) {
              console.error('CSV file does not have the correct headers.');
              setIsOpenAlert(true);
              return;
            }

            const hasInvalidRow = employees.some(
              (employee) => !employee.name?.trim()
                || !employee.email?.trim()
                || !employee.permission?.trim(),
            );

            if (hasInvalidRow) {
              console.error('CSV contains rows with empty required fields.');
              setIsOpenAlert(true);
              return;
            }

            const formattedMembers = employees.map((employee) => ({
              ...employee,
              permissionLevel: roleLowerMapNameToId(employee.permission?.toLowerCase() as string),
            }));

            dispatch(onboardingActions.setInviteMembers(formattedMembers as Array<TMinimalMember>));
            setValue(name, employees);
          },
          error: (error) => {
            console.error('Error parsing CSV:', error);
            setIsOpenAlert(true);
          },
        });
      }
    },
    [name, setValue],
  );

  const handleCloseAlert = useCallback(() => {
    setIsOpenAlert(false);
  }, []);

  return (
    <>
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <UploadArea
            accept={{ 'text/csv': ['.csv'] }}
            file={field.value}
            error={!!error}
            onDrop={handleDrop}
            data={getValues().csv}
            {...other}
          />
        )}
      />
      <Snackbar
        open={isOpenAlert}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity="error" sx={{ width: '100%' }}>
          {t('csvMustContain')}
        </Alert>
      </Snackbar>
    </>
  );
};

export { UploadCSV };
